import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Grid,
  ImageList,
  ImageListItem,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Paper,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  PhotoCamera as PhotoCameraIcon,
  Note as NoteIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { VehicleConditionAssessment, VehicleConditionPhoto, VehicleConditionNote } from '../../types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchConditionPhotoTypes } from '../../store/conditionPhotoTypeSlice';
const DEFAULT_PHOTO_TYPES = [
  { value: 'exterior_front', label: 'Avant' },
  { value: 'exterior_rear', label: 'Arrière' },
  { value: 'exterior_left', label: 'Côté gauche' },
  { value: 'exterior_right', label: 'Côté droit' },
  { value: 'interior_dashboard', label: 'Tableau de bord' },
  { value: 'interior_seats', label: 'Sièges' },
  { value: 'interior_trunk', label: 'Coffre' },
  { value: 'damage_detail', label: 'Détail dommage' },
  { value: 'other', label: 'Autre' },
];

interface AssessmentViewModalProps {
  assessment: VehicleConditionAssessment | null;
  assessments: VehicleConditionAssessment[];
  open: boolean;
  onClose: () => void;
  onEdit?: (assessment: VehicleConditionAssessment) => void;
  onCompare?: (assessment1: VehicleConditionAssessment, assessment2: VehicleConditionAssessment) => void;
}

const AssessmentViewModal: React.FC<AssessmentViewModalProps> = ({
  assessment,
  assessments,
  open,
  onClose,
  onEdit,
  onCompare
}) => {
  const dispatch = useAppDispatch();
  const { types: conditionPhotoTypes } = useAppSelector(state => state.conditionPhotoTypes);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  useEffect(() => {
    if (open && conditionPhotoTypes.length === 0) {
      dispatch(fetchConditionPhotoTypes());
    }
  }, [open, conditionPhotoTypes.length, dispatch]);

  const defaultPhotoTypeLookup = useMemo(() => {
    return DEFAULT_PHOTO_TYPES.reduce<Record<string, string>>((acc, type) => {
      acc[type.value] = type.label;
      return acc;
    }, {});
  }, []);

  const photoTypeLookup = useMemo(() => {
    const lookup = { ...defaultPhotoTypeLookup };
    conditionPhotoTypes.forEach((type) => {
      lookup[type.code] = type.name;
    });
    return lookup;
  }, [conditionPhotoTypes, defaultPhotoTypeLookup]);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<VehicleConditionPhoto | null>(null);

  // Find current assessment index
  useEffect(() => {
    if (assessment && assessments.length > 0) {
      const index = assessments.findIndex(a => a.id === assessment.id);
      setCurrentIndex(index);
    }
  }, [assessment, assessments]);

  const currentAssessment = currentIndex >= 0 ? assessments[currentIndex] : null;
  const clientDisplayName = currentAssessment
    ? currentAssessment.sub_client_display_name ||
      currentAssessment.sub_client?.display_name ||
      currentAssessment.sub_client?.name ||
      [currentAssessment.sub_client?.nom, currentAssessment.sub_client?.prenom].filter(Boolean).join(' ') ||
      currentAssessment.sub_client?.company_name ||
      ''
    : '';
  const parentClientName = currentAssessment
    ? currentAssessment.sub_client_client_name ||
      currentAssessment.sub_client?.client?.name ||
      currentAssessment.sub_client?.company_name ||
      ''
    : '';

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < assessments.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleImageClick = (photo: VehicleConditionPhoto) => {
    setLightboxImage(photo);
    setLightboxOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return '#4caf50';
      case 'fair': return '#8bc34a';
      case 'poor': return '#ff9800';
      case 'critical': return '#f44336';
      default: return '#2196f3';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'good': return 'Bon';
      case 'fair': return 'Correct';
      case 'poor': return 'Mauvais';
      case 'critical': return 'Critique';
      default: return 'Inconnu';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good': return <CheckCircleIcon />;
      case 'fair': return <CheckCircleIcon />;
      case 'poor': return <WarningIcon />;
      case 'critical': return <ErrorIcon />;
      default: return <CancelIcon />;
    }
  };

  const getPhotoTypeLabel = (type: string) => {
    return photoTypeLookup[type] || type;
  };

  if (!currentAssessment) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6">État des Lieux #{currentAssessment.id}</Typography>
              <Chip
                label={getStatusLabel(currentAssessment.status)}
                icon={getStatusIcon(currentAssessment.status)}
                sx={{
                  bgcolor: getStatusColor(currentAssessment.status),
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {/* Navigation */}
              {assessments.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}
                    size="small"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {currentIndex + 1} / {assessments.length}
                  </Typography>
                  <IconButton
                    onClick={handleNext}
                    disabled={currentIndex === assessments.length - 1}
                    size="small"
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </>
              )}
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Information */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Informations générales
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CalendarIcon color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Date d'évaluation
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {currentAssessment.assessment_date
                        ? format(new Date(currentAssessment.assessment_date), 'dd MMMM yyyy', { locale: fr })
                        : 'Non spécifiée'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <PersonIcon color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Créé par
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {currentAssessment.created_by_name || currentAssessment.created_by_user?.name || 'Utilisateur'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <PersonIcon color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Client associé
                      </Typography>
                    </Box>
                    {currentAssessment.sub_client_id ? (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {clientDisplayName || 'Non renseigné'}
                        </Typography>
                        {parentClientName && (
                          <Typography variant="body2" color="text.secondary">
                            {parentClientName}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Aucun client lié
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Photos */}
            {currentAssessment.photos && currentAssessment.photos.length > 0 && (
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <PhotoCameraIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Photos ({currentAssessment.photos.length})
                    </Typography>
                  </Box>
                  <ImageList cols={3} gap={8} sx={{ mb: 0 }}>
                    {currentAssessment.photos.map((photo) => (
                      <ImageListItem
                        key={photo.id}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8,
                            transform: 'scale(1.02)',
                            transition: 'all 0.2s'
                          }
                        }}
                        onClick={() => handleImageClick(photo)}
                      >
                        <img
                          src={photo.file_url || `/api/storage/vehicle-assessments/${photo.file_path}`}
                          alt={photo.description || getPhotoTypeLabel(photo.type)}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover',
                            borderRadius: '8px'
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            p: 1,
                            borderBottomLeftRadius: '8px',
                            borderBottomRightRadius: '8px'
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {getPhotoTypeLabel(photo.type)}
                          </Typography>
                          {photo.description && (
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                              {photo.description}
                            </Typography>
                          )}
                        </Box>
                      </ImageListItem>
                    ))}
                  </ImageList>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {currentAssessment.notes && currentAssessment.notes.length > 0 && (
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <NoteIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Notes ({currentAssessment.notes.length})
                    </Typography>
                  </Box>
                  <List disablePadding>
                    {currentAssessment.notes.map((note, index) => (
                      <React.Fragment key={note.id}>
                        <ListItem
                          sx={{
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            mb: 1,
                            borderLeft: '4px solid',
                            borderLeftColor: 'primary.main'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Note #{index + 1}
                                </Typography>
                                {note.type && (
                                  <Chip
                                    label={note.type}
                                    size="small"
                                    sx={{ height: '20px', fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {note.content}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < currentAssessment.notes!.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}

            {/* Signature */}
            {currentAssessment.signature_path && (
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      ✍️ Signature
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      border: '2px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'background.paper',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 150
                    }}
                  >
                    <img
                      src={currentAssessment.signature_url || `/api/storage/vehicle-assessments/signatures/${currentAssessment.signature_path}`}
                      alt="Signature"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 200,
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        console.error('Signature image failed to load:', currentAssessment.signature_path);
                        e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="100"%3E%3Crect fill="%23ddd" width="200" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3ESignature non disponible%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* No Photos or Notes Message */}
            {(!currentAssessment.photos || currentAssessment.photos.length === 0) &&
              (!currentAssessment.notes || currentAssessment.notes.length === 0) &&
              !currentAssessment.signature_path && (
                <Alert severity="info">
                  Aucune photo ou note pour cet état des lieux.
                </Alert>
              )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Fermer
          </Button>
          {onEdit && (
            <Button onClick={() => onEdit(currentAssessment)} variant="contained">
              Modifier
            </Button>
          )}
          {onCompare && currentIndex > 0 && (
            <Button
              onClick={() => onCompare(assessments[currentIndex - 1], currentAssessment)}
              variant="contained"
              color="secondary"
            >
              Comparer avec précédent
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Lightbox for full-size image */}
      {lightboxOpen && lightboxImage && (
        <Dialog
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {getPhotoTypeLabel(lightboxImage.type)}
              </Typography>
              <IconButton onClick={() => setLightboxOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={lightboxImage.file_url || `/api/storage/vehicle-assessments/${lightboxImage.file_path}`}
                alt={lightboxImage.description || getPhotoTypeLabel(lightboxImage.type)}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
              />
              {lightboxImage.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {lightboxImage.description}
                </Typography>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default AssessmentViewModal;

